import { create } from 'zustand'
import type { ForecastRun, AgentPrediction } from '../types'

type FormatterStatus = 'idle' | 'reformatting' | 'ready' | 'error'

type FormatterPreview = {
  question_id?: string | null
  output?: {
    binary_question?: string
    resolution_criteria?: string
    end_date?: string
    is_valid?: boolean
    error_message?: string
    validation_message?: string
    [k: string]: any
  }
} | null

type FormatterSearchState = {
  status: 'idle' | 'searching' | 'complete'
  query?: string
  num_results?: number
}

type SearchResult = { url?: string; title?: string; snippet?: string }
type SearchEntry = { query: string; results: SearchResult[] }

interface ForecastState {
  currentJobId: string | null
  currentQuestionId: string | null
  currentRuns: ForecastRun[]
  agentPredictions: AgentPrediction[]
  activeAgentStates: Record<number, {
    status: 'pending' | 'running' | 'complete'
    reasoning: string
    searches: SearchEntry[]
    visited_domains: string[]
  }>
  supervisorReasoning: string
  supervisorSearches: SearchEntry[]
  supervisorVisitedDomains: string[]
  supervisorStageStarted: boolean
  predictionStageStarted: boolean
  predictionStageComplete: boolean
  supervisorStageComplete: boolean
  finalStageComplete: boolean
  formatterStatus: FormatterStatus
  formatterPreview: FormatterPreview
  formatterSearch: FormatterSearchState
  setCurrentJobId: (id: string | null) => void
  setCurrentQuestionId: (id: string | null) => void
  setCurrentRuns: (runs: ForecastRun[]) => void
  addRun: (run: ForecastRun) => void
  setAgentPredictions: (predictions: AgentPrediction[]) => void
  updateAgentState: (agentId: number, updates: Partial<ForecastState['activeAgentStates'][number]>) => void
  setSupervisorReasoning: (reasoning: string) => void
  setSupervisorSearches: (searches: SearchEntry[]) => void
  setSupervisorVisitedDomains: (domains: string[]) => void
  setSupervisorStageStarted: (started: boolean) => void
  setPredictionStageStarted: (started: boolean) => void
  setPredictionStageComplete: (complete: boolean) => void
  setSupervisorStageComplete: (complete: boolean) => void
  setFinalStageComplete: (complete: boolean) => void
  setFormatterStatus: (status: FormatterStatus) => void
  setFormatterPreview: (preview: FormatterPreview) => void
  setFormatterSearch: (state: FormatterSearchState) => void
  reset: () => void
}

export const useForecastStore = create<ForecastState>((set) => ({
  currentJobId: null,
  currentQuestionId: null,
  currentRuns: [],
  agentPredictions: [],
  activeAgentStates: {},
  supervisorReasoning: '',
  supervisorSearches: [],
  supervisorVisitedDomains: [],
  supervisorStageStarted: false,
  predictionStageStarted: false,
  predictionStageComplete: false,
  supervisorStageComplete: false,
  finalStageComplete: false,
  formatterStatus: 'idle',
  formatterPreview: null,
  formatterSearch: { status: 'idle' },
  setCurrentJobId: (id) => set({ currentJobId: id }),
  setCurrentQuestionId: (id) => set({ currentQuestionId: id }),
  setCurrentRuns: (runs) => set({ currentRuns: runs }),
  addRun: (run) => set((state) => ({ currentRuns: [...state.currentRuns, run] })),
  setAgentPredictions: (predictions) => set({ agentPredictions: predictions }),
  updateAgentState: (agentId, updates) =>
    set((state) => {
      const currentState = state.activeAgentStates[agentId] || {
        status: 'pending' as const,
        reasoning: '',
        searches: [],
        visited_domains: [],
      }
      return {
        activeAgentStates: {
          ...state.activeAgentStates,
          [agentId]: {
            ...currentState,
            ...updates,
          } as ForecastState['activeAgentStates'][number],
        },
      }
    }),
  setSupervisorReasoning: (reasoning) => set({ supervisorReasoning: reasoning }),
  setSupervisorSearches: (searches) => set({ supervisorSearches: searches }),
  setSupervisorVisitedDomains: (domains) => set({ supervisorVisitedDomains: domains }),
  setSupervisorStageStarted: (started) => set({ supervisorStageStarted: started }),
  setPredictionStageStarted: (started) => set({ predictionStageStarted: started }),
  setPredictionStageComplete: (complete) => set({ predictionStageComplete: complete }),
  setSupervisorStageComplete: (complete) => set({ supervisorStageComplete: complete }),
  setFinalStageComplete: (complete) => set({ finalStageComplete: complete }),
  setFormatterStatus: (status) => set({ formatterStatus: status }),
  setFormatterPreview: (preview) => set({ formatterPreview: preview }),
  setFormatterSearch: (state) => set({ formatterSearch: state }),
  reset: () =>
    set({
      currentJobId: null,
      currentQuestionId: null,
      currentRuns: [],
      agentPredictions: [],
      activeAgentStates: {},
      supervisorReasoning: '',
      supervisorSearches: [],
      supervisorVisitedDomains: [],
      supervisorStageStarted: false,
      predictionStageStarted: false,
      predictionStageComplete: false,
      supervisorStageComplete: false,
      finalStageComplete: false,
      formatterStatus: 'idle',
      formatterPreview: null,
      formatterSearch: { status: 'idle' },
    }),
}))

